import { create } from "zustand"
import { subscribeWithSelector } from "zustand/middleware"
import _xor from "lodash/xor"
import axios from "axios"
import { generateDeliveryProduct } from "./utils"
import dayjs from "dayjs"
import debounce from "lodash/debounce"
import _uniqBy from "lodash/uniqBy"

const fetchTanks = (location) => async () => {
  if (!location) return []
  const { data } = await axios.get(`/locations/${location}/tanks/`)
  const sites = {}
  const tanks = data
  tanks.sort((a, b) => (a.run_out < b.run_out ? -1 : 1))

  return tanks
}

const mapRecords = (record) => {
  const terminalPref = record?.preferences?.[0]?.default_terminal
  const terminalLabelPref =
    record?.preferences?.[0]?.default_terminal_details?.name
  const supplierPref = record?.preferences?.[0]?.default_supplier
  const loadingNoPref = record?.preferences?.[0]?.default_loading_number
  return {
    ...record,
    tank_id: record.id,
    terminal:
      record.terminal ??
      (terminalPref ? { id: terminalPref, name: terminalLabelPref } : null),
    supplier: record.supplier ?? (supplierPref ? { id: supplierPref } : null),
    loading_number:
      record.loading_number ?? (loadingNoPref ? { id: loadingNoPref } : null),
  }
}

const INITIAL_STATE = {
  newRecord: {},
  newPrice: {},
  records: [],
  prices: [],
  pickupsString: "",
  pickups: [],
  customer: null,
  location: null,
  startDate: null,
  floatingPrice: false,
}

const useStore = create(
  subscribeWithSelector((set, get) => ({
    newRecord: {},
    newPrice: {},
    newPrefs: [],
    records: [],
    prices: [],
    pickupsString: "",
    pickups: [],
    customer: null,
    location: null,
    startDate: null,
    floatingPrice: false,

    resetState: () => set((state) => ({ ...state, ...INITIAL_STATE })),
    setState: (newState = {}) => set((state) => ({ ...state, ...newState })),

    setFloatingPrice: (floatingPrice) => set({ floatingPrice }),

    setRecords: (records) => {
      set({ records })
    },
    removeRecord: (index) => {
      const { records } = get()
      const newRecords = records.filter((_, i) => i !== index)
      set({ records: newRecords })
    },
    setStartDate: (startDate) =>
      set({
        startDate,
        startDateText: dayjs(startDate).format("YYYY-MM-DD HH:mmZ"),
      }),
    setCustomer: (customer) => {
      const { customer: oldCustomer } = get()
      if (oldCustomer === customer) return
      return set({ customer, prices: [], records: [] })
    },
    setLocation: async (location) => {
      const { location: oldLocation, records: oldRecords } = get()
      // dont update anything if same location is chosen

      if (oldLocation === location) return

      const records = (await fetchTanks(location)()).map(mapRecords)
      set({ location, records, prices: [] })
    },
    fetchLocation: async (location) => {
      if (!location) return
      const { records: oldRecords, location: oldLocation } = get()
      set({ location })
      const records = ((await fetchTanks(location)()) ?? []).map(mapRecords)
      if (location !== oldLocation) return set({ records, prices: [] })
      const uniqueRecords = _uniqBy(
        (oldRecords ?? [])
          .filter((record) => !!record.quantity)
          .concat(records ?? []),
        (record) =>
          `${record.tank_name}_${
            record.product.product_id ?? record.product.id
          }_${record?.deliveryProductId}`
      )

      set({ records: uniqueRecords, prices: [] })
    },
    getFinalDeliveryProducts: () => {
      const { newRecord, records, prices, newPrice, newPrefs } = get()
      const products = [generateDeliveryProduct(newRecord, newPrice, newPrefs)]

      const oldProducts = records.map((item, index) =>
        generateDeliveryProduct(item, prices[index], item.newPrefs)
      )

      return [...products, ...oldProducts].filter((a) => !!a?.gross_quantity)
    },

    updateRecord: (type, index, preferenceIndex) => async (value) => {
      const {
        newRecord,
        newPrefs,
        records,
        customer,
        location,
        startDateText,
      } = get()
      const currentPrefs =
        index === undefined ? newPrefs : records?.[index]?.newPrefs ?? []

      if (preferenceIndex !== undefined) {
        currentPrefs[preferenceIndex] = {
          ...(currentPrefs[preferenceIndex] ?? {}),
          [type]: value,
        }
      }

      const newStuff =
        preferenceIndex == undefined
          ? { [type]: value }
          : { newPrefs: currentPrefs }

      if (index === undefined) {
        const newRecordPayload = { ...newRecord, ...newStuff }
        set({ newRecord: newRecordPayload })
        if (!customer || !newRecordPayload?.product?.id) return
        const pricePayload = {
          customer,
          location,
          effective_at: startDateText,
          pickups: [
            {
              product: newRecordPayload?.product?.id,
              terminal: newRecordPayload?.terminal?.id,
              supplier: newRecordPayload?.supplier?.id,
            },
          ],
        }

        if (!location) return
        const { data } = await axios.post(`best-buy/load-prices/`, pricePayload)
        const floatingPrice = data?.floating_price ?? false

        return set({ newPrice: data?.prices?.[0], floatingPrice })
      }

      const newRecords = Object.assign([], records)
      newRecords[index] = { ...newRecords[index], ...newStuff }

      set({
        records: newRecords,
      })
    },
    onProductChange: (index) => (e) => {
      const { updateRecord, onSplashQuantityChange, records } = get()
      if (index === undefined) set({ newRecord: {} })
      else {
        const newRecords = Object.assign([], records)
        newRecords[index] = {}
        if (records[index].tank_id) {
          newRecords[index].new_product_id =
            e.product_id ?? records[index].product.id
          newRecords[index].tank_id = records[index].tank_id
          newRecords[index].tank_name = records[index].tank_name
          newRecords[index].deliveryProductId = records[index].deliveryProductId
          if (newRecords[index].deliveryProductId) {
            newRecords[index].quantity = records[index].quantity
          }
          newRecords[index].terminal = records[index].terminal
          newRecords[index].supplier = records[index].supplier
          newRecords[index].loading_number = records[index].loading_number
          newRecords[index].preferences = records[index].preferences
        }
        set({
          records: newRecords,
        })
      }

      updateRecord(
        "product",
        index
      )({
        id: e.product,
        tank_id: e.tank_id,
        blended: e.blended,
        components: e.components,
      })

      if (e.tank_id) updateRecord("tank_id", index)(e.tank_id)
      if (e.blended) {
        onSplashQuantityChange(index)({
          target: { value: records?.[index]?.quantity?.value ?? 0 },
        })
      }
    },
    onQuantityChange: (index) => (e) => {
      const { updateRecord } = get()
      updateRecord("quantity", index)({ value: e.target.value, error: "" })
    },
    onTerminalChange: (recordIndex) => (preferenceIndex) => (e) => {
      const { updateRecord } = get()
      updateRecord(
        "terminal",
        recordIndex,
        preferenceIndex
      )({
        id: e.terminal,
        name: e.terminal_label,
      })
      updateRecord("supplier", recordIndex, preferenceIndex)(null)
      updateRecord("loading_number", recordIndex, preferenceIndex)(null)
    },
    onSupplierChange: (recordIndex) => (preferenceIndex) => (e) => {
      const { updateRecord } = get()
      updateRecord("supplier", recordIndex, preferenceIndex)({ id: e.supplier })
      updateRecord("loading_number", recordIndex, preferenceIndex)(null)
    },
    onLoadNumberChange: (recordIndex) => (preferenceIndex) => (e) => {
      const { updateRecord } = get()
      updateRecord(
        "loading_number",
        recordIndex,
        preferenceIndex
      )({ id: e.loading_number_id })
    },
    onPriceChange: (index) => (e) => {
      const { newPrice, prices } = get()
      if (index === undefined)
        return set({
          newPrice: { ...newPrice, value: e.target.value },
        })

      const newPrices = Object.assign([], prices)
      newPrices[index] = { ...newPrices[index], value: e.target.value }
      set({
        prices: newPrices,
      })
      // updateRecord("price", index)({ value: e.target.value })
    },
    onAddNewPreference: (index) => (e) => {
      const { newPrefs, records } = get()

      if (index === undefined)
        return set({ newPrefs: [...(newPrefs ?? []), {}] })

      const prefs = records[index]?.newPrefs ?? []
      records[index] = { ...records[index], newPrefs: [...prefs, {}] }
      set({ records: [...records] })
    },
    onRemovePreference: (index) => (prefIndex) => (e) => {
      const { newPrefs, records } = get()
      newPrefs?.splice?.(prefIndex, 1)
      if (index === undefined) return set({ newPrefs: [...newPrefs] })

      const prefs = records[index]?.newPrefs ?? []
      prefs.splice(prefIndex, 1)
      records[index] = {
        ...records[index],
        newPrefs: [...prefs],
      }
      set({ records: [...records] })
    },
    validateNewRow: () => {
      const { newRecord, updateRecord } = get()
      // Validation
      if (!newRecord?.product)
        return updateRecord("product")({ error: "Enter Product" })
      if (!newRecord?.quantity?.value)
        return updateRecord("quantity")({ error: "Enter quantity" })
    },
    validateRowsForTerminal: () => {
      const { records, updateRecord, newRecord } = get()
      let isValid = true
      if (newRecord?.product?.id && !!newRecord?.quantity?.value) {
        if (newRecord?.components) {
          const hasError = Object.values(newRecord?.components ?? {}).some(
            (component) => !component?.terminal
          )
          if (hasError) {
            isValid = false
            updateRecord("terminal")({ error: "Enter Terminal" })
          }
        } else {
          if (!newRecord?.terminal?.id) {
            isValid = false
            updateRecord("terminal")({ error: "Enter Terminal" })
          }
        }
      }
      records.forEach((record, index) => {
        if (record?.product?.id && !!record?.quantity?.value) {
          if (record?.components) {
            const hasError = Object.values(record?.components ?? {}).some(
              (component) => !component?.terminal
            )
            if (hasError) {
              isValid = false
              updateRecord("terminal", index)({ error: "Enter Terminal" })
            }
          } else {
            if (!record?.terminal?.id) {
              isValid = false
              updateRecord("terminal", index)({ error: "Enter Terminal" })
            }
          }
        }
      })
      return isValid
    },
    validateRowsForLoadingNumber: () => {
      const { records, updateRecord, newRecord } = get()
      let isValid = true
      if (newRecord?.product?.id && !!newRecord?.quantity?.value) {
        if (newRecord?.components) {
          const hasError = Object.values(newRecord?.components ?? {}).some(
            (component) => !component?.loading_number
          )
          if (hasError) {
            isValid = false
            updateRecord("loading_number")({ error: "Enter Loading Number" })
          }
        } else {
          if (!newRecord?.loading_number?.id) {
            isValid = false
            updateRecord("loading_number")({ error: "Enter Loading Number" })
          }
        }
      }
      records.forEach((record, index) => {
        if (record?.product?.id && !!record?.quantity?.value) {
          if (record?.components) {
            const hasError = Object.values(record?.components ?? {}).some(
              (component) => !component?.loading_number?.id
            )
            if (hasError) {
              isValid = false
              updateRecord(
                "loading_number",
                index
              )({
                error: "Enter Loading Number",
              })
            }
          } else {
            if (!record?.loading_number?.id) {
              isValid = false
              updateRecord(
                "loading_number",
                index
              )({
                error: "Enter Loading Number",
              })
            }
          }
        }
      })
      return isValid
    },
    onAddNewRow: () => {
      const { records, prices, newRecord, updateRecord, newPrice } = get()
      // Validation
      if (!newRecord?.product) {
        return updateRecord("product")({ error: "Enter Product" })
      }
      if (!newRecord?.quantity?.value) {
        return updateRecord("quantity")({ error: "Enter quantity" })
      }

      records.push(newRecord)
      prices.push(newPrice)
      set((state) => ({
        ...state,
        newRecord: {},
        newPrice: {},
        records: [...records],
        prices: [...prices],
      }))
    },
    setBestPrices: (prices) => {
      // SplashChanges
      const { records } = get()

      const newRecords = records.map((record, index) => {
        const productId = record.product.id
        if (!prices[productId]) return record
        return {
          ...record,
          terminal: prices[productId]?.pricing?.terminal,
          supplier: prices[productId]?.pricing?.supplier,
          loading_number:
            prices[productId]?.pricing?.loading_numbers?.[0] ??
            record.loading_number,
        }
      })
      set({ records: newRecords })
    },
    onSplashQuantityChange: (index) => (e) => {
      if (e?.target?.value === undefined) return
      const { updateRecord, records, newRecord } = get()
      const currentRecord = index === undefined ? newRecord : records[index]

      const percentages = [
        currentRecord?.product?.components?.product_1_percentage,
        currentRecord?.product?.components?.product_2_percentage,
        currentRecord?.product?.components?.product_3_percentage,
        currentRecord?.product?.components?.product_4_percentage,
      ]

      const splashValues = percentages.map((percentage) =>
        !e.target.value ? 0 : parseFloat(e.target.value) * (percentage ?? 0)
      )

      const ingredients = splashValues.map(
        (_, i) => currentRecord?.product?.components?.[`product_${i + 1}`]
      )

      const components = {}
      ingredients.forEach((ingredient, i) => {
        if (ingredient) {
          components[ingredient] = {
            ...(currentRecord?.components?.[ingredient] ?? {}),
            product: ingredient,
            quantity: Math.ceil(splashValues[i] / 100),
          }
        }
      })

      updateRecord("components", index)(components)
      updateRecord("quantity", index)({ value: e.target.value, error: "" })
    },

    onSplashChange: (index) => (componentIndex, value) => {
      const { updateRecord, records, newRecord } = get()
      const currentRecord = index === undefined ? newRecord : records[index]
      const components = currentRecord?.components ?? {}
      components[componentIndex] = {
        ...(components[componentIndex] ?? {}),
        ...value,
      }

      // const componentMap = currentRecord?.componentMap ?? {}
      // componentMap[componentIndex] = {
      //   ...(components[componentIndex] ?? {}),
      //   ...value
      // }

      updateRecord("components", index)(components)
      // updateRecord("componentMap", index)(componentMap)
    },
  }))
)

// generate pickups whenever records change
const unsubRecords = useStore.subscribe(
  (state) => state.records,
  debounce((records, previous) => {
    const pickups = (records ?? [])
      .map((record) => ({
        product: record?.product?.id,
        terminal: record?.terminal?.id,
        supplier: record?.supplier?.id,
      }))
      .filter((pickup) => !!pickup.product)
    useStore.setState({
      pickupsString: JSON.stringify(pickups),
      pickups,
    })
  }, 200)
)

// generate prices whenever pickups change
const unsubPickups = useStore.subscribe(
  (state) => `${state.pickupsString}-${state.startDateText}`,
  async () => {
    const {
      customer,
      location,
      pickups,
      records,
      startDateText,
      prices: oldPrices,
    } = useStore.getState()
    if (!customer || !location || !pickups.length) return
    // TODO Need to add a loader here
    const payload = {
      customer,
      location,
      pickups,
      effective_at: startDateText,
    }
    if (!location || !startDateText) return
    const { data: prices } = await axios.post(`best-buy/load-prices/`, payload)
    const floatingPrice = prices?.floating_price ?? false
    const newPrices = (prices?.prices ?? []).map((price, index) => {
      let oldPrice = oldPrices?.[index] ?? {}
      if (oldPrice.value) price.price_with_fees = parseFloat(oldPrice.value)
      if (oldPrice.product !== pickups?.[index]?.product) oldPrice = {}
      if (records?.[index]?.price) price.price_with_fees = records[index].price
      return {
        ...oldPrice,
        ...price,
        customer,
        location,
        product: pickups?.[index]?.product,
        cost_basis: price?.cost_basis ?? null,
      }
    })
    useStore.setState({ prices: newPrices, floatingPrice })
  }
)

export default useStore
